import {Collapse, Typography} from '@mui/material';
import {AurSwitch} from '../../common/aur-switch/AurSwitch';
import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {GroupPermValueItem, SwitchWithLabel} from '../AddEditPermissionGroup';
import {
  PermissionCode,
  PermissionItem,
  PermissionType,
} from '../../../utils/permissionListUtils';
import {isEmptyInstance} from '../../../utils/utils';
import {
  PERMISSION_GROUP_CATEGORY,
  PERMISSION_TYPE,
} from '../../../constants/constants';
import _ from 'lodash';
export interface PermissionCategoryProps {
  permCategory: PERMISSION_GROUP_CATEGORY;
  permType: PermissionType;
  title?: string;
  footerNotes?: string;
  desc?: string;
  defaultIsOverridenChecked?: boolean;
  isOverriden?: boolean;
  isPermCategoryEnabled?: boolean;
  preloadedPermSwitches?: GroupPermValueItem;
  permItemArray: PermissionItem[];
  defaultCheckedPerms?: PermissionCode[];
  onChangeOverridePerm?: (permType: PermissionType, isChecked: boolean) => void;
  onChangePerms?: (
    permType: PermissionType,
    selectedPerms: GroupPermValueItem
  ) => void;
}

const PermissionCategoryView: React.FC<PermissionCategoryProps> = (
  props: PermissionCategoryProps
) => {
  const {
    permCategory,
    permType,
    title,
    footerNotes,
    desc,
    defaultIsOverridenChecked = false,
    isOverriden = false,
    isPermCategoryEnabled = true,
    preloadedPermSwitches = {},
    permItemArray,
    defaultCheckedPerms = [],
    onChangeOverridePerm,
    onChangePerms,
  } = props;

  const [isOverridenChecked, setIsOverridenChecked] = useState<boolean>(
    defaultIsOverridenChecked
  );

  const [permSwitches, setPermSwitches] = useState<GroupPermValueItem>(
    preloadedPermSwitches
  );

  const filteredPermItemArray = useMemo(
    () =>
      permItemArray?.filter((permItem: PermissionItem) =>
        (permItem?.category ?? []).includes(permCategory)
      ),
    [permCategory, permItemArray]
  );

  useEffect(() => {
    setIsOverridenChecked(defaultIsOverridenChecked);
  }, [defaultIsOverridenChecked]);

  useEffect(() => {
    !isEmptyInstance(preloadedPermSwitches) &&
      isEmptyInstance(permSwitches) &&
      setPermSwitches(preloadedPermSwitches);
  }, [permSwitches, preloadedPermSwitches]);

  useEffect(() => {
    !_.isEqual(preloadedPermSwitches, permSwitches) &&
      onChangePerms?.(permType, permSwitches);
  }, [onChangePerms, permSwitches, permType, preloadedPermSwitches]);

  const isClientBI =
    permType === PERMISSION_TYPE.BUSINESS_INTELLIGENCE &&
    permCategory === PERMISSION_GROUP_CATEGORY.CLIENT;

  const onChangeOverriden = useCallback(() => {
    setIsOverridenChecked((prevIsOverridenChecked: boolean) => {
      const updatedIsOverridenChecked = !prevIsOverridenChecked;

      if (updatedIsOverridenChecked) {
        const hasAnySelected = Object.values(permSwitches).some(
          (p) => p.checked
        );
        if (!hasAnySelected) {
          setPermSwitches((prePermSwitches: GroupPermValueItem) => {
            const updatedPermSwitches = {...prePermSwitches};

            for (const [key, value] of Object.entries(updatedPermSwitches)) {
              if (
                defaultCheckedPerms
                  .map((item) => item.toString())
                  .includes(value.permissionCode)
              ) {
                updatedPermSwitches[key].checked = true;
              }
            }
            return updatedPermSwitches;
          });
        }
      } else {
        setPermSwitches((prePermSwitches: GroupPermValueItem) => {
          const updated = {...prePermSwitches};
          Object.keys(updated).forEach((key) => {
            updated[key].checked = false;
          });
          return updated;
        });
      }

      onChangeOverridePerm?.(permType, updatedIsOverridenChecked);
      return updatedIsOverridenChecked;
    });
  }, [defaultCheckedPerms, onChangeOverridePerm, permSwitches, permType]);

  const handlePermSwitchChange = useCallback(
    (permissionId: string) => {
      setPermSwitches((prev) => {
        const updatedPermSwitches = {
          ...prev,
          [permissionId]: {
            ...prev[permissionId],
            checked: !(prev[permissionId]?.checked ?? false),
          },
        };

        const hasAnySelected = Object.values(updatedPermSwitches).some(
          (p) => p.checked
        );

        if (!hasAnySelected && isOverriden && isOverridenChecked) {
          onChangeOverriden();
        }

        return updatedPermSwitches;
      });
    },
    [isOverriden, isOverridenChecked, onChangeOverriden]
  );

  const midpoint = useMemo(
    () => Math.ceil(filteredPermItemArray?.length / 2),
    [filteredPermItemArray]
  );

  const renderPermissions = useCallback(() => {
    return (
      <div className={'switch-div-container'}>
        <div
          className={`switch-div ${permCategory === PERMISSION_GROUP_CATEGORY.CLIENT && 'client-switch-div'}`}
        >
          <div className={'switch-div-1'}>
            {filteredPermItemArray
              ?.slice(0, midpoint)
              .map((permissionItem: PermissionItem) => {
                const {
                  permissionId,
                  name,
                  footerNotes,
                  infoText,
                  disableToolTip = false,
                } = permissionItem;
                return (
                  <SwitchWithLabel
                    key={permissionId}
                    label={name}
                    infoText={infoText}
                    state={permSwitches?.[permissionId]?.checked ?? false}
                    footerNotes={footerNotes}
                    onChange={() => handlePermSwitchChange(permissionId)}
                    permCategory={permCategory}
                    disableToolTip={disableToolTip}
                  />
                );
              })}
          </div>
          <div className={'switch-div-2'}>
            {filteredPermItemArray
              ?.slice(midpoint)
              .map((permissionItem: PermissionItem) => {
                const {permissionId, name, footerNotes, infoText} =
                  permissionItem;
                return (
                  <SwitchWithLabel
                    key={permissionId}
                    label={name}
                    infoText={infoText}
                    state={permSwitches?.[permissionId]?.checked ?? false}
                    footerNotes={footerNotes}
                    onChange={() => handlePermSwitchChange(permissionId)}
                    permCategory={permCategory}
                  />
                );
              })}
          </div>
        </div>
      </div>
    );
  }, [filteredPermItemArray, midpoint, permSwitches, handlePermSwitchChange]);

  return (
    <div className={'permission-category-container'}>
      {
        <div
          className={`enable-switch-container ${isOverriden && isOverridenChecked ? '' : 'no-border'} ${isClientBI && 'remove-border-top'}`}
        >
          {isEmptyInstance(title) ? null : (
            <Typography
              className={'typo-text subhead2_size title-text'}
              role={'heading'}
              aria-level={2}
            >
              {title}
            </Typography>
          )}
          {!isEmptyInstance(footerNotes) && (
            <Typography className={'typo-text body4_size bi-footer'}>
              {footerNotes}
            </Typography>
          )}
          {isEmptyInstance(desc) ? null : (
            <Typography
              className="typo-text body2_size subtitle-text"
              data-testid="subtitle"
            >
              {desc}
            </Typography>
          )}
          {isOverriden ? (
            <div className={'switch-on-row'}>
              <AurSwitch
                disabled={!isPermCategoryEnabled}
                checked={isOverridenChecked}
                onChange={onChangeOverriden}
                dataTestId={`toggle-${permType.toLowerCase()}-switch`}
                ariaLabel={`${t('aria.toggle')} ${t('eca.permisssionPanel.eca')}`}
              />
              {permType === PERMISSION_TYPE.ECI ? (
                <Typography
                  className={'typo-text subhead4_size'}
                  aria-live={'polite'}
                >
                  {`${t('eca.permisssionPanel.' + (isOverridenChecked ? 'On' : 'Off'))}`}
                </Typography>
              ) : (
                <Typography
                  className={'typo-text subhead4_size'}
                  aria-live={'polite'}
                >
                  {t('eca.permisssionPanel.override')}
                </Typography>
              )}
            </div>
          ) : null}
        </div>
      }

      {isOverriden ? (
        <Collapse in={isOverridenChecked} timeout={'auto'} unmountOnExit>
          {renderPermissions()}
        </Collapse>
      ) : (
        renderPermissions()
      )}
    </div>
  );
};

export default PermissionCategoryView;
